import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Upload as UploadIcon,
  OpenInNew as OpenInNewIcon,
  Visibility as ViewIcon,
  AttachFile as AttachFileIcon,
  LinkOff as LinkOffIcon,
  FolderOpen as FolderOpenIcon
} from '@mui/icons-material';
import api from '../api';

const ATTACH_WARN_MESSAGE =
  'Attach a property or tenant so this file shows on that record and in the portal when shared.';

function driveUrlFor(doc) {
  if (doc?.googleDriveUrl) return doc.googleDriveUrl;
  if (doc?.googleDriveId) {
    return `https://drive.google.com/file/d/${doc.googleDriveId}/view`;
  }
  return null;
}

/**
 * Thin entity Files strip (DOC-03 F7). Lists registry docs for a property, tenant or lease.
 * Upload prefills locked entity IDs; does not set sharedWithPortal.
 * With leaseId it lists only that lease's files and can link existing Documents hub files.
 */
const EntityDocumentsStrip = ({
  propertyId = '',
  tenantId = '',
  unitId = '',
  leaseId = '',
  entityLabel = '',
  onOpenInHub = null
}) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [uploadData, setUploadData] = useState({
    title: '',
    category: 'Lease',
    file: null
  });
  const [linkOpen, setLinkOpen] = useState(false);
  const [hubDocs, setHubDocs] = useState([]);
  const [hubLoading, setHubLoading] = useState(false);
  const [hubSearch, setHubSearch] = useState('');
  const [linkingId, setLinkingId] = useState('');

  const isLeaseMode = Boolean(leaseId);
  const hasEntity = Boolean(propertyId || tenantId || leaseId);

  const loadDocs = async () => {
    if (!hasEntity) {
      setDocs([]);
      return;
    }
    try {
      setLoading(true);
      setError('');
      const res = await api.get('/api/documents', isLeaseMode ? { params: { leaseId } } : undefined);
      const list = Array.isArray(res.data) ? res.data : [];
      const filtered = list.filter((doc) => {
        if (isLeaseMode) return doc.leaseId === leaseId;
        if (propertyId && doc.propertyId === propertyId) return true;
        if (tenantId && doc.tenantId === tenantId) return true;
        return false;
      });
      setDocs(filtered);
    } catch (err) {
      console.error('❌ EntityDocumentsStrip fetch failed:', err);
      setError('Could not load files');
      setDocs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, tenantId, leaseId]);

  const handleUpload = async () => {
    if (!uploadData.title || !uploadData.category || !uploadData.file) {
      setError('Title, category, and file are required');
      return;
    }
    try {
      setUploading(true);
      setError('');
      const formData = new FormData();
      formData.append('file', uploadData.file);
      formData.append('title', uploadData.title);
      formData.append('description', '');
      formData.append('category', uploadData.category);
      formData.append('propertyId', propertyId || '');
      formData.append('tenantId', tenantId || '');
      if (isLeaseMode) {
        formData.append('leaseId', leaseId);
        formData.append('unitId', unitId || '');
      }
      formData.append('tags', '');
      formData.append('expiryDate', '');
      // sharedWithPortal stays default false on backend
      await api.post('/api/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadOpen(false);
      setUploadData({ title: '', category: 'Lease', file: null });
      await loadDocs();
    } catch (err) {
      console.error('❌ EntityDocumentsStrip upload failed:', err);
      setError('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const openLinkDialog = async () => {
    setLinkOpen(true);
    setHubSearch('');
    try {
      setHubLoading(true);
      const res = await api.get('/api/documents');
      setHubDocs(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('❌ EntityDocumentsStrip hub fetch failed:', err);
      setError('Could not load your documents');
      setHubDocs([]);
    } finally {
      setHubLoading(false);
    }
  };

  const linkableDocs = useMemo(() => {
    const q = hubSearch.trim().toLowerCase();
    return hubDocs
      .filter((doc) => doc.leaseId !== leaseId)
      .filter((doc) => !q || [doc.title, doc.category, doc.fileName].filter(Boolean).join(' ').toLowerCase().includes(q));
  }, [hubDocs, hubSearch, leaseId]);

  const handleLinkExisting = async (doc) => {
    try {
      setLinkingId(doc.id);
      setError('');
      const body = { leaseId };
      if (!doc.propertyId && propertyId) body.propertyId = propertyId;
      if (!doc.tenantId && tenantId) body.tenantId = tenantId;
      if (!doc.unitId && unitId) body.unitId = unitId;
      await api.put(`/api/documents/${doc.id}/attach`, body);
      setLinkOpen(false);
      await loadDocs();
    } catch (err) {
      console.error('❌ EntityDocumentsStrip link failed:', err);
      setError(err.response?.data?.error || 'Could not attach that document');
    } finally {
      setLinkingId('');
    }
  };

  const handleUnlink = async (doc) => {
    try {
      setError('');
      await api.put(`/api/documents/${doc.id}/attach`, { leaseId: null });
      await loadDocs();
    } catch (err) {
      console.error('❌ EntityDocumentsStrip unlink failed:', err);
      setError('Could not remove that file from this lease');
    }
  };

  return (
    <Box sx={{ mt: 2, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2">
          Files{entityLabel ? ` · ${entityLabel}` : ''}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {typeof onOpenInHub === 'function' && (
            <Button size="small" startIcon={<FolderOpenIcon />} onClick={onOpenInHub}>
              View in Documents
            </Button>
          )}
          {isLeaseMode && (
            <Button size="small" startIcon={<AttachFileIcon />} onClick={openLinkDialog}>
              Attach from Documents
            </Button>
          )}
          <Button
            size="small"
            startIcon={<UploadIcon />}
            onClick={() => setUploadOpen(true)}
            disabled={!hasEntity}
          >
            Upload
          </Button>
        </Box>
      </Box>

      {error && !uploadOpen && !linkOpen && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <CircularProgress size={20} />
      ) : docs.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {isLeaseMode
            ? 'No files on this lease yet. Upload the signed lease, or attach one already in Documents.'
            : 'No files attached yet. Upload a lease or insurance file here.'}
        </Typography>
      ) : (
        <List dense disablePadding>
          {docs.slice(0, 8).map((doc) => {
            const driveUrl = driveUrlFor(doc);
            return (
              <ListItem
                key={doc.id}
                disableGutters
                secondaryAction={
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {doc.fileUrl && (
                      <Tooltip title="Open">
                        <IconButton size="small" onClick={() => window.open(doc.fileUrl, '_blank')}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {driveUrl && (
                      <Tooltip title="Open in Drive">
                        <IconButton size="small" onClick={() => window.open(driveUrl, '_blank')}>
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {isLeaseMode && (
                      <Tooltip title="Remove from this lease (stays in Documents)">
                        <IconButton size="small" aria-label={`Remove ${doc.title || 'file'} from this lease`} onClick={() => handleUnlink(doc)}>
                          <LinkOffIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                }
              >
                <ListItemText
                  primary={doc.title || 'Untitled'}
                  secondary={
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                      {doc.category && <Chip label={doc.category} size="small" />}
                      {doc.status && <Chip label={doc.status} size="small" variant="outlined" />}
                    </Box>
                  }
                  secondaryTypographyProps={{ component: 'div' }}
                />
              </ListItem>
            );
          })}
        </List>
      )}
      {docs.length > 8 && typeof onOpenInHub === 'function' && (
        <Button size="small" onClick={onOpenInHub} sx={{ mt: 0.5 }}>
          See all {docs.length} in Documents
        </Button>
      )}

      <Dialog open={uploadOpen} onClose={() => !uploading && setUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload file</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            {isLeaseMode
              ? 'Saved to Documents and linked to this lease, its property and tenant.'
              : `Attached to this record. ${ATTACH_WARN_MESSAGE}`}
          </Alert>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <TextField
            fullWidth
            margin="normal"
            label="Title"
            value={uploadData.title}
            onChange={(e) => setUploadData({ ...uploadData, title: e.target.value })}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Category</InputLabel>
            <Select
              value={uploadData.category}
              label="Category"
              onChange={(e) => setUploadData({ ...uploadData, category: e.target.value })}
            >
              <MenuItem value="Lease">Lease</MenuItem>
              <MenuItem value="Contract">Contract</MenuItem>
              <MenuItem value="Permit">Permit</MenuItem>
              <MenuItem value="Insurance">Insurance</MenuItem>
              <MenuItem value="Form">Form</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" component="label" fullWidth sx={{ mt: 2 }}>
            Choose file
            <input
              type="file"
              hidden
              accept=".pdf,.doc,.docx"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) setUploadData({ ...uploadData, file });
              }}
            />
          </Button>
          {uploadData.file && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Selected: {uploadData.file.name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleUpload} disabled={uploading}>
            {uploading ? <CircularProgress size={20} /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={linkOpen} onClose={() => !linkingId && setLinkOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Attach from Documents</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Pick a file you already have in Documents. It stays in Documents and also shows on this lease.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <TextField
            fullWidth
            size="small"
            margin="dense"
            label="Search by title"
            value={hubSearch}
            onChange={(e) => setHubSearch(e.target.value)}
          />
          {hubLoading ? (
            <CircularProgress size={20} sx={{ mt: 2 }} />
          ) : linkableDocs.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {hubDocs.length === 0 ? 'You have no documents yet. Use Upload instead.' : 'No matching documents.'}
            </Typography>
          ) : (
            <List dense sx={{ maxHeight: 360, overflowY: 'auto' }}>
              {linkableDocs.map((doc) => (
                <ListItem
                  key={doc.id}
                  disableGutters
                  secondaryAction={
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleLinkExisting(doc)}
                      disabled={Boolean(linkingId)}
                    >
                      {linkingId === doc.id ? <CircularProgress size={16} /> : 'Attach'}
                    </Button>
                  }
                >
                  <ListItemText
                    primary={doc.title || 'Untitled'}
                    secondary={[doc.category, doc.leaseId ? 'On another lease' : null].filter(Boolean).join(' · ')}
                    sx={{ pr: 10 }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkOpen(false)} disabled={Boolean(linkingId)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EntityDocumentsStrip;
